import {CommonModule} from '@angular/common';
import {HTTP_INTERCEPTORS} from '@angular/common/http';
import {CUSTOM_ELEMENTS_SCHEMA, ModuleWithProviders, NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {RouterModule} from '@angular/router';
import {L10nTranslationModule} from 'angular-l10n';
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {BsModalRef, ModalModule} from 'ngx-bootstrap/modal';
import {PaginationModule} from 'ngx-bootstrap/pagination';
import {BlockUIComponent} from './components/block-ui/block-ui.component';
import {ImpersonateUserComponent} from './components/impersonate-user/impersonate-user.component';
import {QuickSearchResultsComponent} from './components/quick-search-results/quick-search-results.component';
import {ScrollToTopComponent} from './components/scroll-to-top/scroll-to-top.component';
import {SendBackResultComponent} from './components/send-back-result/send-back-result.component';
import {TopMenuComponent} from './components/top-menu/top-menu.component';
import {DateTimePickerDirective} from './directives/date-time-picker.directive';
import {MustBeAuthenticatedGuard} from './guards/must-be-authenticated.guard';
import {AppLayoutComponent} from './pages/app-layout/app-layout.component';
import {AuthLayoutComponent} from './pages/auth-layout/auth-layout.component';
import {ErrorLayoutComponent} from './pages/error-layout/error-layout.component';
import {AvatarPipe} from './pipes/avatar.pipe';
import {ClientsFilterPipe} from './pipes/clients-filter.pipe';
import {DateMoment} from './pipes/date-moment.pipe';
import {ImpersonableUsersFilterCountPipe} from './pipes/impersonable-users-filter-count.pipe';
import {ImpersonableUsersFilterPipe} from './pipes/impersonable-users-filter.pipe';
import {MessagePipe} from './pipes/message.pipe';
import {MomentPipe} from './pipes/moment.pipe';
import {Nl2brPipe} from './pipes/nl2br.pipe';
import {NotDiscardedPipe} from './pipes/not-discarded.pipe';
import {PaginatePipe} from './pipes/paginate.pipe';
import {TernaryPipe} from './pipes/ternary.pipe';
import {TimeAgoPipe} from './pipes/time-ago.pipe';
import {AppSettingsService} from './services/app-settings.service';
import {Auth0AuthenticationService} from './services/auth0-authentication.service';
import {Auth0ManagementService} from './services/auth0-management.service';
import {BlockUIService} from './services/block-ui.service';
import {BodyService} from './services/body.service';
import {DateTimeFormatService} from './services/date-time-format.service';
import {ExternalLinksService} from './services/external-links.service';
import {ImpersonableUsersService} from './services/impersonable-users.service';
import {InterceptorSpinnerService} from './services/interceptor-spinner.service';
import {LayoutService} from './services/layout.service';
import {PasswordStrengthService} from './services/password-strength.service';
import {QuickSearchService} from './services/quick-search.service';
import {ScrollToTopService} from './services/scroll-to-top.service';
import {TimezonesService} from './services/timezones.service';
import {UserContextService} from './services/user-context.service';
import {IsAfterValidatorDirective} from './validators/is-after/directive';
import {IsBeforeValidatorDirective} from './validators/is-before/directive';
import {IsBetweenValidatorDirective} from './validators/is-between/directive';
import {IsSameOrAfterValidatorDirective} from './validators/is-same-or-after/directive';
import {MomentValidatorDirective} from './validators/moment/directive';
import { TooltipComponent } from './components/tooltip/tooltip.component';
import {NgDatatableModule} from '@storever/ng-datatable';
import {ToastModule} from 'primeng/toast';
import {ModalComponent, ModalConfimationModule} from '@storever/modal-confirmation';
import {FormSelectComponent} from './components/dynamic-form/form-select/form-select.component';
import {FormInputComponent} from './components/dynamic-form/form-input/form-input.component';
import {DynamicFormComponent} from './components/dynamic-form/dynamic-form.component';
import {DynamicFieldDirective} from './directives/dynamic-field.directive';
import { FormCheckBoxComponent } from './components/dynamic-form/form-check-box/form-check-box.component';
import { FormTextAreaComponent } from './components/dynamic-form/form-text-area/form-text-area.component';
import { FormDatePickerComponent } from './components/dynamic-form/form-date-picker/form-date-picker.component';
import {ContextualMenuContainerComponent} from './components/contextual-menu-container/contextual-menu-container.component';
import {ContextMenuComponent} from './components/contextual-menu-container/context-menu/context-menu.component';
import {FooterFormComponent} from './components/footer-form/footer-form.component';
import {ElementGroupComponent} from './components/element-group/element-group.component';
import {HeaderPageComponent} from './components/header-page/header-page.component';
import {ControlErrorsDirective} from './directives/control-errors.directive';
import {ControlErrorsComponent} from './components/control-errors/control-errors.component';
import { FormTypeaheadComponent } from './components/dynamic-form/form-typeahead/form-typeahead.component';
import { HeaderModalComponent } from './components/header-modal/header-modal.component';
import { HeaderSubtitleComponent } from './components/header-subtitle/header-subtitle.component';
import {PrintPlayerComponent} from './components/print-player/print-player.component';
import { LoadingDataComponent } from './components/loading-data/loading-data.component';
import {FormLinkComponent} from './components/dynamic-form/form-link/form-link.component';
import { TabsComponent } from './components/generic-tabs/tabs/tabs.component';
import { TabComponent } from './components/generic-tabs/tab/tab.component';
import { GenericTabsComponent } from './components/generic-tabs/generic-tabs.component';
import { TestComponent } from './components/test/test.component';
import { GenericFilterComponent } from './components/generic-datatable/generic-filter/generic-filter.component';
import {PlayerListComponent} from '../site/components/player-list/player-list.component';
import {GenericDatatableComponent} from './components/generic-datatable/generic-datatable.component';
import {SubscriptionEditComponent} from '../site/components/subscription-edit/subscription-edit.component';
import {MiscellaneousEditComponent} from '../site/components/miscellaneous-edit/miscellaneous-edit.component';
import {NetworkEditComponent} from '../site/components/network-edit/network-edit.component';
import { EditTabsComponent } from './components/generic-tabs/edit-tabs/edit-tabs.component';
import {AddressesEditComponent} from '../site/components/addresses-edit/addresses-edit.component';
import {ContactEditComponent} from '../site/components/contact-edit/contact-edit.component';
import {StatusToOffComponent} from '../site/components/modal-status-to-off/status-to-off.component';
import {SiteAddPlayerComponent} from '../site/components/site-add-player/site-add-player.component';
import { ButtonActionComponent } from './components/button-action/button-action.component';
import {EnableDisableFormgroupService} from './services/enable-disable-formgroup.service';
import {RoleCanActivateGuard} from './guards/role-can-activate.guard';
import {TransporterService} from './services/transporter.service';
import { PlayerConfigurationEditComponent } from './components/player-configuration-edit/player-configuration-edit.component';
import { AddOptionsComponent } from './components/player-configuration-edit/add-options/add-options.component';
import { EditOptionsComponent } from './components/player-configuration-edit/edit-options/edit-options.component';
import { AddOptionsItemComponent } from './components/player-configuration-edit/add-options/add-options-item/add-options-item.component';
import {FilterByNamePipe} from './pipes/filter.pipe';
import {PlayerConfigurationService} from './services/player-configuration.service';
import { InputOptionsComponent } from './components/dynamic-form/input-options/input-options.component';
import {ShippingsListComponent} from '../shipping/page/list/shippings-list.component';
import {ShippingAddComponent} from '../shipping/component/shipping-add/shipping-add.component';
import {ShippingDetailComponent} from '../shipping/page/detail/shipping-detail.component';
import {ContractsListComponent} from '../contract/pages/list/contracts-list.component';
import {CompanyDisplayComponent} from '../company/components/company-display/company-display.component';
import {GetConfigSharedService} from './services/get-config-shared.service';
import { LinkContractModalComponent } from './components/link-contract-modal/link-contract-modal.component';
import { PublicationListComponent } from '../site/components/publication-list/publication-list.component';
import {OrderDetailComponent} from '../order/pages/details/order-detail.component';
import {OrderAddComponent} from '../order/components/order-add/order-add.component';
import {CancelOrderComponent} from '../order/components/cancel-order/cancel-order.component';
import { DuplicateContractModalComponent } from './components/duplicate-contract-modal/duplicate-contract-modal.component';
import { InvoicesListComponent } from './components/invoices-list/invoices-list.component';
import { ActionButtonComponent } from './components/header-page/action-button/action-button.component';
import { BreadcrumsComponent } from './components/header-page/breadcrums/breadcrums.component';
import {TypeaheadModule} from 'ngx-bootstrap/typeahead';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {BsDatepickerModule} from 'ngx-bootstrap/datepicker';
import { CloseBillingSubscriptionComponent } from './components/close-billing-subscription/close-billing-subscription.component';
import { DatatableHeaderBtnComponent } from './components/datatable-header-btn/datatable-header-btn.component';
import {SubscriptionListComponent} from './components/subscription-list/subscription-list.component';
import {ManuallyCloseOrderComponent} from './components/manually-close-order/manually-close-order.component';
import {NgProgressHttpModule} from 'ngx-progressbar/http';
import {NgProgressModule} from 'ngx-progressbar';
import { FormDateRangePickerComponent } from './components/dynamic-form/form-date-range-picker/form-date-range-picker.component';
import { AnomalyPlayerReturnComponent } from './components/anomaly-player-return/anomaly-player-return.component';
import {ResetOrderModalComponent} from '../order/components/reset-order-modal/reset-order-modal.component';
import {FormMultiselectComponent} from './components/dynamic-form/form-multiselect/form-multiselect.component';
import {NgxSelectModule} from 'ngx-select-ex';
import {CompaniesListComponent} from '../company/pages/list/companies-list.component';
import { ModalEditSfProjectComponent } from './components/modal-edit-sf-project/modal-edit-sf-project.component';
import {ProjectListComponent} from '../site/components/project-list/project-list.component';
import {MergeSiteDialogComponent} from './components/merge-site-dialog/merge-site-dialog.component';
import {AlertModule} from 'ngx-bootstrap/alert';
import {SubscriptionListSimpleComponent} from './components/subscription-list/subscription-list-simple.component';
import { InvoicesManagementModalComponent } from './components/invoices-management-modal/invoices-management-modal.component';
import {CloseForcedOrderModalComponent} from '../order/components/close-forced-order-modal/close-forced-order-modal.component';
import {InternalUserGuard} from './guards/internal-user.guard';

export const httpInterceptorProviders = [
  // tslint:disable:no-unused-variable
  {provide: HTTP_INTERCEPTORS, useClass: InterceptorSpinnerService, multi: true},
];

@NgModule({
    imports: [
        BsDropdownModule,
        BsDatepickerModule,
        CommonModule,
        FormsModule,
        ModalConfimationModule,
        ModalModule,
        NgDatatableModule,
        NgProgressModule.withConfig({
            spinnerPosition: 'right',
            color: '#0080ff'
        }),
        NgProgressHttpModule,
        PaginationModule,
        ReactiveFormsModule,
        RouterModule,
        ToastModule,
        TypeaheadModule,
        TooltipModule.forRoot(),
        L10nTranslationModule,
        NgxSelectModule,
        AlertModule,

    ],
    exports: [
        // Components
        ModalEditSfProjectComponent,
        ResetOrderModalComponent,
        CloseForcedOrderModalComponent,
        BlockUIComponent,
        ContextMenuComponent,
        GenericFilterComponent,
        ContextMenuComponent,
        ContextualMenuContainerComponent,
        DynamicFormComponent,
        ElementGroupComponent,
        FooterFormComponent,
        FormInputComponent,
        FormLinkComponent,
        FormSelectComponent,
        FormTypeaheadComponent,
        GenericDatatableComponent,
        HeaderPageComponent,
        HeaderModalComponent,
        HeaderSubtitleComponent,
        ImpersonateUserComponent,
        LoadingDataComponent,
        PrintPlayerComponent,
        ScrollToTopComponent,
        SendBackResultComponent,
        TopMenuComponent,
        TooltipComponent,
        TabsComponent,
        TabComponent,
        ButtonActionComponent,
        QuickSearchResultsComponent,
        // Directives
        ControlErrorsDirective,
        DateTimePickerDirective,
        DynamicFieldDirective,
        // Pages
        AppLayoutComponent,
        AuthLayoutComponent,
        ErrorLayoutComponent,
        // Pipes
        AvatarPipe,
        ClientsFilterPipe,
        FilterByNamePipe,
        DateMoment,
        ImpersonableUsersFilterPipe,
        ImpersonableUsersFilterCountPipe,
        MessagePipe,
        MomentPipe,
        Nl2brPipe,
        NotDiscardedPipe,
        PaginatePipe,
        TernaryPipe,
        TimeAgoPipe,
        // Validators
        IsAfterValidatorDirective,
        IsBeforeValidatorDirective,
        IsBetweenValidatorDirective,
        IsSameOrAfterValidatorDirective,
        MomentValidatorDirective,
        // modules
        ModalConfimationModule,
        NgDatatableModule,
        ToastModule,
        TooltipModule,
        GenericTabsComponent,
        EditTabsComponent,
        ModalModule,
        GenericFilterComponent,
        LinkContractModalComponent,
        DuplicateContractModalComponent,
        OrderDetailComponent,
        OrderAddComponent,
        CancelOrderComponent,
        InvoicesListComponent,
        BreadcrumsComponent,
        BsDatepickerModule,
        DatatableHeaderBtnComponent,
        SubscriptionListComponent,
        SubscriptionListSimpleComponent,
        CompaniesListComponent,
        ManuallyCloseOrderComponent,
        ContractsListComponent,
        L10nTranslationModule,
        FormMultiselectComponent,
        StatusToOffComponent,
        ActionButtonComponent,
        InvoicesManagementModalComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    entryComponents: [
    AddressesEditComponent,
    ContactEditComponent,
    ControlErrorsComponent,
    FormCheckBoxComponent,
    FormDatePickerComponent,
    FormInputComponent,
    FormLinkComponent,
    FormSelectComponent,
    FormTextAreaComponent,
    FormTypeaheadComponent,
    ModalComponent,
    TestComponent,
    GenericFilterComponent,
    PlayerListComponent,
    SubscriptionEditComponent,
    MiscellaneousEditComponent,
    NetworkEditComponent,
    SiteAddPlayerComponent,
    PlayerConfigurationEditComponent,
    ShippingsListComponent,
    ShippingDetailComponent,
    ShippingAddComponent,
    CompanyDisplayComponent,
    ContractsListComponent,
    SubscriptionListComponent,
    SubscriptionListSimpleComponent,
    CompaniesListComponent,
    LinkContractModalComponent,
    ModalEditSfProjectComponent,
    DuplicateContractModalComponent,
    PublicationListComponent,
    ProjectListComponent,
    OrderDetailComponent,
    InvoicesListComponent,
    BreadcrumsComponent,
    InvoicesManagementModalComponent
  ],
    declarations: [
    // Components
    ResetOrderModalComponent,
    CloseForcedOrderModalComponent,
    AddressesEditComponent,
    BlockUIComponent,
    ContactEditComponent,
    ContextMenuComponent,
    ContextualMenuContainerComponent,
    ContextualMenuContainerComponent,
    ContextMenuComponent,
    ControlErrorsComponent,
    DynamicFormComponent,
    ElementGroupComponent,
    FooterFormComponent,
    FormCheckBoxComponent,
    FormDatePickerComponent,
    FormInputComponent,
    FormLinkComponent,
    FormSelectComponent,
    FormMultiselectComponent,
    FormTypeaheadComponent,
    FormCheckBoxComponent,
    FormTextAreaComponent,
    GenericDatatableComponent,
    HeaderPageComponent,
    HeaderModalComponent,
    HeaderSubtitleComponent,
    ImpersonateUserComponent,
    LoadingDataComponent,
    PrintPlayerComponent,
    ScrollToTopComponent,
    SendBackResultComponent,
    TopMenuComponent,
    TooltipComponent,
    FormTypeaheadComponent,
    LoadingDataComponent,
    TabsComponent,
    TabComponent,
    GenericTabsComponent,
    TestComponent,
    GenericFilterComponent,
    PlayerListComponent,
    SubscriptionEditComponent,
    MiscellaneousEditComponent,
    NetworkEditComponent,
    PlayerConfigurationEditComponent,
    SiteAddPlayerComponent,
    ShippingsListComponent,
    ShippingDetailComponent,
    ShippingAddComponent,
    CompanyDisplayComponent,
    ContractsListComponent,
    SubscriptionListComponent,
    SubscriptionListSimpleComponent,
    CompaniesListComponent,
    QuickSearchResultsComponent,
    // Directives
    ControlErrorsDirective,
    DateTimePickerDirective,
    DynamicFieldDirective,
    // Pages
    AppLayoutComponent,
    AuthLayoutComponent,
    ErrorLayoutComponent,
    // Pipes
    AvatarPipe,
    ClientsFilterPipe,
    FilterByNamePipe,
    DateMoment,
    ImpersonableUsersFilterPipe,
    ImpersonableUsersFilterCountPipe,
    MessagePipe,
    MomentPipe,
    Nl2brPipe,
    NotDiscardedPipe,
    PaginatePipe,
    TernaryPipe,
    TimeAgoPipe,
    // Validators
    IsAfterValidatorDirective,
    IsBeforeValidatorDirective,
    IsBetweenValidatorDirective,
    IsSameOrAfterValidatorDirective,
    MomentValidatorDirective,
    EditTabsComponent,
    StatusToOffComponent,
    ButtonActionComponent,
    AddOptionsComponent,
    EditOptionsComponent,
    AddOptionsItemComponent,
    InputOptionsComponent,
    LinkContractModalComponent,
    PublicationListComponent,
    ProjectListComponent,
    OrderDetailComponent,
    OrderAddComponent,
    CancelOrderComponent,
    DuplicateContractModalComponent,
    InvoicesListComponent,
    ActionButtonComponent,
    BreadcrumsComponent,
    CloseBillingSubscriptionComponent,
    DatatableHeaderBtnComponent,
    ManuallyCloseOrderComponent,
    FormDateRangePickerComponent,
    AnomalyPlayerReturnComponent,
    ModalEditSfProjectComponent,
    MergeSiteDialogComponent,
    InvoicesManagementModalComponent
  ]
})
export class SharedModule {
  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
      providers: [
        // Guards
        MustBeAuthenticatedGuard,
        InternalUserGuard,
        RoleCanActivateGuard,
        // Services
        AppSettingsService,
        Auth0AuthenticationService,
        Auth0ManagementService,
        BlockUIService,
        BodyService,
        DateTimeFormatService,
        ExternalLinksService,
        ImpersonableUsersService,
        LayoutService,
        PasswordStrengthService,
        // { provide: BrowserXhr, useClass: ProgressBrowserXhr },
        httpInterceptorProviders,
        QuickSearchService,
        ScrollToTopService,
        TimezonesService,
        TransporterService,
        UserContextService,
        EnableDisableFormgroupService,
        PlayerConfigurationService,
        GetConfigSharedService,
        BsModalRef,
      ]
    };
  }
}
